import { LOGO_MAX_BYTES, LOGO_SIZE_PX } from '@guildrise/shared';

/**
 * Сжатие логотипа перед отправкой.
 *
 * Фото с телефона весит 5 МБ. После обрезки, масштабирования и WebP —
 * около 20 КБ. Без этого шага загрузка на мобильной сети была бы
 * мучительной, а сервер принимал бы мегабайты ради картинки 256×256.
 *
 * Размер и лимит файла: packages/shared/src/schemas/guild.ts.
 */

/** Качество подбирается вниз, пока файл не уложится в лимит. */
const QUALITY_STEPS = [0.8, 0.65, 0.5, 0.35] as const;

export async function compressToWebp(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);

  try {
    // Обрезка по центру в квадрат: иначе портрет растянулся бы в круге.
    const side = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = LOGO_SIZE_PX;
    canvas.height = LOGO_SIZE_PX;

    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('canvas 2d недоступен');

    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, LOGO_SIZE_PX, LOGO_SIZE_PX);

    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, quality);
      if (blob !== null && blob.size <= LOGO_MAX_BYTES) return blob;
    }

    throw new Error('картинку не удалось сжать до лимита');
  } finally {
    // Битмап держит память до сборки мусора — освобождаем сразу.
    bitmap.close();
  }
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', quality);
  });
}
